import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useCheckout,
  useOrderBump,
  useProducts,
  type CheckoutData,
  type CheckoutLineItem,
} from "@tagadapay/plugin-sdk/v2";
import type { Bundle, CheckoutItem } from "@/src/types/checkout";
import toast from "react-hot-toast";
import { useConfig } from "./useConfig";

interface UseCheckoutLogicProps {
  checkoutToken: string;
}

interface CheckoutSummaryItem {
  variantId: string;
  originalPrice: number;
  totalPrice: number;
  quantity: number;
  amount: number;
  currency: string;
  variant: {
    name: string;
    imageUrl: string;
  };
}

interface OrderBump {
  id: string;
  variantId: string;
  name: string;
  quantity: number;
  totalPrice: number;
  imageUrl: string;
  selected: boolean;
}

const BUNDLE_CONFIGS = [
  {
    id: "bundle2",
    variantKey: "double" as const,
    name: "x1 Sonicfit™ + Cellulite Cream Package",
    quantity: 1,
    bestValue: true,
    variantName: "MOST POPULAR",
    percentageSavings: 50,
    image: "https://img.funnelish.com/41178/571163/1754185730-a2.png",
    originalPrice: 27990,
  },
  {
    id: "bundle1",
    variantKey: "simple" as const,
    name: "x1 Sonicfit™",
    quantity: 1,
    bestValue: false,
    variantName: "Regular",
    percentageSavings: 50,
    image: "https://img.funnelish.com/41178/571163/1754185745-a4.png",
    originalPrice: 27990,
  },
  {
    id: "bundle3",
    variantKey: "triple" as const,
    name: "x1 Sonicfit™ + x2 Cellulite Cream Package",
    quantity: 1,
    bestValue: false,
    variantName: "Special Deal",
    percentageSavings: 50,
    image: "https://img.funnelish.com/41178/571163/1754185766-a1.png",
    originalPrice: 27990,
  },
  {
    id: "bundle4",
    variantKey: "quadruple" as const,
    name: "x2 Sonicfit™",
    quantity: 1,
    bestValue: false,
    variantName: "Quadruple Deal",
    percentageSavings: 50,
    image: "https://img.funnelish.com/41178/571163/1754185756-a3.png",
    originalPrice: 67980,
  },
] as const;

const DEFAULT_BUNDLE_ID = "bundle2";

function mapSdkLineToCheckoutSummaryItem(
  item: any,
  defaultCurrency: string,
): CheckoutSummaryItem {
  return {
    variantId: item.variantId,
    quantity: item.quantity ?? 1,
    amount: item.amount ?? 0,
    currency: item.currency ?? defaultCurrency,
    originalPrice: item.originalPrice ?? item.amount ?? 0,
    totalPrice: item.totalPrice ?? item.amount ?? 0,
    variant: {
      name: item.variant?.name ?? "",
      imageUrl: item.variant?.imageUrl ?? "",
    },
  };
}

export function useCheckoutLogic({ checkoutToken }: UseCheckoutLogicProps) {
  const { pluginConfig, storeId } = useConfig();

  const {
    checkout: sdkCheckout,
    init: sdkInit,
    updateLineItems: sdkUpdateLineItems,
    applyPromotionCode: sdkApplyPromotionCode,
    removePromotion: sdkRemovePromotion,
    updateCustomerAndSessionInfo: sdkUpdateCustomerAndSessionInfo,
    error: sdkError,
    refresh: sdkRefresh,
    setItemQuantity: sdkSetItemQuantity,
    isLoading: sdkIsLoading,
  } = useCheckout({
    enabled: true,
    checkoutToken,
  });

  const { getVariant: sdkGetVariant } = useProducts({
    enabled: true,
    includeVariants: true,
    includePrices: true,
  });

  const { toggle: toggleFirstOrderBump } = useOrderBump({
    checkoutToken,
    offerId: pluginConfig.orderBumpIdExpeditedShipping,
  });

  const { toggle: toggleSecondOrderBump } = useOrderBump({
    checkoutToken,
    offerId: pluginConfig.orderBumpIdLifetimeProtection,
  });

  const [bundleMutationPending, setBundleMutationPending] = useState(false);
  const [orderBumpMutationPending, setOrderBumpMutationPending] =
    useState(false);
  const [optimisticBundleId, setOptimisticBundleId] = useState<string | null>(
    null,
  );
  const [optimisticCartLines, setOptimisticCartLines] = useState<
    CheckoutSummaryItem[] | null
  >(null);
  const [optimisticBumpSelection, setOptimisticBumpSelection] = useState<
    Partial<Record<string, boolean>>
  >({});
  const [isInitFailed, setIsInitFailed] = useState(false);
  const [couponCodeInput, setCouponCodeInput] = useState("");
  const [appliedCouponCode, setAppliedCouponCode] = useState<string | null>(
    null,
  );
  const [appliedPromotionId, setAppliedPromotionId] = useState<string | null>(
    null,
  );
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);

  const hasInitializedRef = useRef(false);
  const checkoutLatestRef = useRef<CheckoutData | undefined>(undefined);
  checkoutLatestRef.current = sdkCheckout;

  const getCheckout = useCallback(
    () => checkoutLatestRef.current,
    [],
  );

  const bundleWorkRef = useRef<Promise<void> | null>(null);
  const bumpWorkRef = useRef<Promise<void> | null>(null);
  const bundleMutationSeqRef = useRef(0);

  const firstItem = sdkCheckout?.summary?.items?.[0];
  const currentVariantId = sdkCheckout?.summary?.items?.find((lineItem: any) =>
    Object.values(pluginConfig.variants).some(
      (variant) => variant === lineItem.variantId,
    ),
  )?.variantId;

  const bundleVariantIds = useMemo(
    () =>
      BUNDLE_CONFIGS.map(
        (config) => pluginConfig.variants[config.variantKey],
      ).filter((variantId): variantId is string => Boolean(variantId)),
    [pluginConfig.variants],
  );

  const orderBumpConfigs = useMemo(
    () => [
      {
        id: "orderBumpIdLifetimeProtection",
        variantId: pluginConfig.orderBumpIdLifetimeProtectionVariant,
        offerId: pluginConfig.orderBumpIdLifetimeProtection,
      },
      {
        id: "orderBumpIdExpeditedShipping",
        variantId: pluginConfig.orderBumpIdExpeditedShippingVariant,
        offerId: pluginConfig.orderBumpIdExpeditedShipping,
      },
    ],
    [pluginConfig],
  );

  const orderBumpToggleByVariantId = useMemo(() => {
    const m = new Map<
      string,
      (selected: boolean) => Promise<{ success?: boolean } | void>
    >();
    if (
      pluginConfig.orderBumpIdExpeditedShippingVariant &&
      toggleFirstOrderBump
    ) {
      m.set(
        pluginConfig.orderBumpIdExpeditedShippingVariant,
        toggleFirstOrderBump,
      );
    }
    if (
      pluginConfig.orderBumpIdLifetimeProtectionVariant &&
      toggleSecondOrderBump
    ) {
      m.set(
        pluginConfig.orderBumpIdLifetimeProtectionVariant,
        toggleSecondOrderBump,
      );
    }
    return m;
  }, [
    pluginConfig.orderBumpIdExpeditedShippingVariant,
    pluginConfig.orderBumpIdLifetimeProtectionVariant,
    toggleFirstOrderBump,
    toggleSecondOrderBump,
  ]);

  const getVariant = useCallback(
    (variantId: string) => {
      if (!sdkGetVariant) {
        console.warn(
          "[useCheckoutLogic] SDK getVariant function is not available",
        );
        return null;
      }
      try {
        return sdkGetVariant(variantId);
      } catch (error) {
        console.error("[useCheckoutLogic] Error in getVariant:", {
          error,
          variantId,
        });
        return null;
      }
    },
    [sdkGetVariant],
  );

  const getVariantPrice = useCallback(
    (
      variantId: string,
      currency: string = pluginConfig.defaultCurrency,
    ): number => {
      const variantData = getVariant(variantId);
      const priceMapping =
        pluginConfig.prices[variantId as keyof typeof pluginConfig.prices];
      const price = variantData?.variant?.prices?.find(
        (p) => p.id === priceMapping,
      );
      return price?.currencyOptions?.[currency]?.amount || 0;
    },
    [pluginConfig, getVariant],
  );

  const createBundles = useCallback(
    (item: CheckoutItem | undefined): Bundle[] => {
      const currency = item?.currency || pluginConfig.defaultCurrency;
      const productName = item?.name || "Product";

      return BUNDLE_CONFIGS.map((config) => {
        const variantId = pluginConfig.variants[config.variantKey];
        const variantData = getVariant(variantId);
        const priceAmount = getVariantPrice(variantId, currency);

        return {
          id: config.id,
          variantId,
          name: config.name,
          quantity: config.quantity,
          totalPrice: priceAmount,
          originalPrice: config.originalPrice,
          images: [config.image],
          bestValue: config.bestValue,
          productName: variantData?.product?.name || productName,
          variantName: config.variantName,
          currency,
          percentageSavings: config.percentageSavings,
        };
      });
    },
    [pluginConfig, getVariant, getVariantPrice],
  );

  const convertToSDKLineItems = useCallback(
    (items: CheckoutSummaryItem[]): CheckoutLineItem[] => {
      return items.map((item) => {
        const priceId =
          pluginConfig.prices[
            item.variantId as keyof typeof pluginConfig.prices
          ];
        return {
          variantId: item.variantId,
          quantity: item.quantity,
          priceId: priceId || "",
        };
      });
    },
    [pluginConfig.prices],
  );

  const clearStaleBundleVariants = useCallback(
    async (nextLineItems: CheckoutLineItem[]) => {
      if (!sdkSetItemQuantity || bundleVariantIds.length === 0) {
        return;
      }

      const nextBundleVariantIds = new Set(
        nextLineItems
          .filter(
            (lineItem) =>
              bundleVariantIds.includes(lineItem.variantId || "") &&
              lineItem.quantity > 0,
          )
          .map((lineItem) => lineItem.variantId),
      );

      const staleBundleVariantIds = Array.from(
        new Set(
          (sdkCheckout?.summary?.items ?? [])
            .map((lineItem: any) => lineItem.variantId)
            .filter(
              (variantId: string) =>
                bundleVariantIds.includes(variantId) &&
                !nextBundleVariantIds.has(variantId),
            ),
        ),
      );

      if (staleBundleVariantIds.length === 0) {
        return;
      }

      await Promise.all(
        staleBundleVariantIds.map((variantId) =>
          sdkSetItemQuantity(variantId, 0),
        ),
      );
    },
    [sdkSetItemQuantity, bundleVariantIds, sdkCheckout?.summary?.items],
  );

  const updateLineItemsInSDK = useCallback(
    async (lineItems: CheckoutLineItem[]) => {
      if (!sdkCheckout?.checkoutSession?.id || !sdkUpdateLineItems) {
        return;
      }

      try {
        await clearStaleBundleVariants(lineItems);
        return await sdkUpdateLineItems(lineItems);
      } catch (error) {
        console.error("[useCheckoutLogic] Error in updateLineItemsInSDK:", {
          error,
          lineItems,
        });
        throw error;
      }
    },
    [
      sdkCheckout?.checkoutSession?.id,
      sdkUpdateLineItems,
      clearStaleBundleVariants,
    ],
  );

  const init = useCallback(
    async (params: Parameters<typeof sdkInit>[0]) => {
      if (!sdkInit) {
        throw new Error("SDK init function is not available");
      }
      try {
        return await sdkInit(params);
      } catch (error) {
        console.error("[useCheckoutLogic] Error in init:", { error, params });
        throw error;
      }
    },
    [sdkInit],
  );

  const refresh = useCallback(async () => {
    if (!sdkRefresh) {
      console.warn("[useCheckoutLogic] SDK refresh function is not available");
      return;
    }
    try {
      return await sdkRefresh();
    } catch (error) {
      console.error("[useCheckoutLogic] Error in refresh:", { error });
      throw error;
    }
  }, [sdkRefresh]);

  const inferPromotionIdFromCheckout = useCallback(
    (code: string) => {
      const normalizedCode = code.trim().toLowerCase();
      if (!normalizedCode) return null;

      const matchingPromotion = sdkCheckout?.availablePromotions?.find(
        (promotion: any) => {
          const name = String(promotion?.name || "").toLowerCase();
          const description = String(
            promotion?.description || "",
          ).toLowerCase();
          return (
            name === normalizedCode || description.includes(normalizedCode)
          );
        },
      );

      return matchingPromotion?.id || null;
    },
    [sdkCheckout?.availablePromotions],
  );

  const applyCoupon = useCallback(async () => {
    const code = couponCodeInput.trim();
    if (!code) {
      setCouponError("Please enter a coupon code.");
      return false;
    }

    if (!sdkCheckout?.checkoutSession?.id || !sdkApplyPromotionCode) {
      setCouponError("Checkout is not ready for coupon application.");
      return false;
    }

    try {
      setIsApplyingCoupon(true);
      setCouponError(null);

      const result = await sdkApplyPromotionCode(code);
      if (result?.success === false) {
        throw new Error(result?.error.message || "Invalid coupon code.");
      }

      const detectedPromotionId =
        result?.promotionId ||
        result?.promotion?.id ||
        result?.id ||
        inferPromotionIdFromCheckout(code);

      setAppliedPromotionId(detectedPromotionId || null);
      setAppliedCouponCode(code);
      await refresh();
      return true;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to apply coupon. Please try again.";
      setCouponError(message);
      return false;
    } finally {
      setIsApplyingCoupon(false);
    }
  }, [
    couponCodeInput,
    sdkCheckout?.checkoutSession?.id,
    sdkApplyPromotionCode,
    inferPromotionIdFromCheckout,
    refresh,
  ]);

  const removeCoupon = useCallback(async () => {
    if (!sdkCheckout?.checkoutSession?.id || !sdkRemovePromotion) {
      setCouponError("Checkout is not ready for coupon removal.");
      return false;
    }

    if (!appliedPromotionId) {
      setCouponError(
        "Unable to remove coupon automatically. Please refresh and try again.",
      );
      return false;
    }

    try {
      setIsApplyingCoupon(true);
      setCouponError(null);

      const result = await sdkRemovePromotion(appliedPromotionId);
      if (result?.success === false) {
        throw new Error(result?.error || "Failed to remove coupon.");
      }

      setAppliedCouponCode(null);
      setAppliedPromotionId(null);
      setCouponCodeInput("");
      await refresh();
      return true;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to remove coupon. Please try again.";
      setCouponError(message);
      return false;
    } finally {
      setIsApplyingCoupon(false);
    }
  }, [
    sdkCheckout?.checkoutSession?.id,
    sdkRemovePromotion,
    appliedPromotionId,
    refresh,
  ]);

  const updateCouponCodeInput = useCallback(
    (value: string) => {
      setCouponCodeInput(value);
      if (couponError) {
        setCouponError(null);
      }
    },
    [couponError],
  );

  const hasActivePromotionDiscount =
    (sdkCheckout?.summary?.totalPromotionAmount || 0) > 0;
  const couponDisplayLabel =
    appliedCouponCode || (hasActivePromotionDiscount ? "Coupon applied" : null);

  const bundles = useMemo(
    () => createBundles(firstItem),
    [firstItem, createBundles],
  );

  const bumpVariantIdsForDisplay = useMemo(
    () => orderBumpConfigs.map((c) => c.variantId),
    [orderBumpConfigs],
  );

  const lineItems = useMemo((): CheckoutSummaryItem[] => {
    const serverItems = sdkCheckout?.summary?.items;
    let base: CheckoutSummaryItem[];

    if (optimisticCartLines?.length) {
      base = optimisticCartLines;
    } else if (serverItems?.length) {
      base = serverItems as unknown as CheckoutSummaryItem[];
    } else {
      base = [];
    }

    const withSynth: CheckoutSummaryItem[] = [...base];
    const currency = pluginConfig.defaultCurrency;

    for (const vid of bumpVariantIdsForDisplay) {
      if (optimisticBumpSelection[vid] !== true) continue;
      const hasLine = withSynth.some((row) => row.variantId === vid);
      if (hasLine) continue;
      const vd = getVariant(vid);
      const priceAmount = getVariantPrice(vid, currency);
      const row = {
        variantId: vid,
        quantity: 1,
        amount: priceAmount,
        currency,
        originalPrice: priceAmount,
        totalPrice: priceAmount,
        variant: {
          name: vd?.variant?.name ?? "",
          imageUrl: vd?.variant?.imageUrl ?? "",
        },
        adjustedAmount: priceAmount,
      } as CheckoutSummaryItem & { adjustedAmount?: number };
      withSynth.push(row as CheckoutSummaryItem);
    }

    return withSynth.filter((row) => {
      if (!bumpVariantIdsForDisplay.includes(row.variantId)) return true;
      const ov = optimisticBumpSelection[row.variantId];
      if (ov === false) return false;
      return true;
    });
  }, [
    sdkCheckout?.summary?.items,
    optimisticCartLines,
    optimisticBumpSelection,
    bumpVariantIdsForDisplay,
    getVariant,
    getVariantPrice,
    pluginConfig.defaultCurrency,
  ]);

  const orderBumpsInCheckout = useMemo((): OrderBump[] => {
    const currency = pluginConfig.defaultCurrency;
    const summaryItems = sdkCheckout?.summary?.items ?? [];

    return orderBumpConfigs.map((config) => {
      const inCart = summaryItems.find(
        (row: any) => row.variantId === config.variantId,
      );
      const variantData = getVariant(config.variantId);
      const ov = optimisticBumpSelection[config.variantId];
      const selected =
        ov !== undefined ? Boolean(ov) : (inCart?.quantity ?? 0) > 0;

      return {
        id: config.id,
        variantId: config.variantId,
        name: inCart?.variant?.name ?? variantData?.variant?.name ?? "",
        quantity: inCart?.quantity ?? 1,
        totalPrice:
          typeof inCart?.adjustedAmount === "number"
            ? inCart.adjustedAmount
            : getVariantPrice(config.variantId, currency),
        imageUrl:
          inCart?.variant?.imageUrl ?? variantData?.variant?.imageUrl ?? "",
        selected,
      };
    });
  }, [
    orderBumpConfigs,
    sdkCheckout?.summary?.items,
    pluginConfig.defaultCurrency,
    getVariant,
    getVariantPrice,
    optimisticBumpSelection,
  ]);

  const selectedBundle = useMemo((): string | null => {
    if (optimisticBundleId) return optimisticBundleId;
    const items = sdkCheckout?.summary?.items;
    if (!items?.length) return null;
    if (currentVariantId && bundles.length > 0) {
      const matching = bundles.find((b) => b.variantId === currentVariantId);
      if (matching) return matching.id;
    }
    return DEFAULT_BUNDLE_ID;
  }, [
    optimisticBundleId,
    sdkCheckout?.summary?.items,
    currentVariantId,
    bundles,
  ]);

  const handleBundleChange = useCallback(
    async (bundleId: string) => {
      if (bundleMutationPending) return;

      const selectedBundleData = bundles.find(
        (bundle: Bundle) => bundle.id === bundleId,
      );
      if (!selectedBundleData) {
        console.warn("[useCheckoutLogic] Bundle not found:", bundleId);
        return;
      }

      const variantData = getVariant(selectedBundleData.variantId);
      if (!variantData?.variant?.id) {
        console.warn(
          "[useCheckoutLogic] Variant data not found for bundle:",
          bundleId,
        );
        return;
      }

      const seq = ++bundleMutationSeqRef.current;

      const allBundleVariantIds = bundles.map((bundle) => bundle.variantId);
      const rawItems = sdkCheckout?.summary?.items ?? [];
      const existingFilteredLineItems = rawItems
        .filter((row: any) => !allBundleVariantIds.includes(row.variantId))
        .map((row: any) =>
          mapSdkLineToCheckoutSummaryItem(row, pluginConfig.defaultCurrency),
        );

      const newLineItem: CheckoutSummaryItem = {
        variantId: variantData.variant.id,
        quantity: 1,
        amount: selectedBundleData.totalPrice,
        currency: pluginConfig.defaultCurrency,
        originalPrice: selectedBundleData.totalPrice,
        totalPrice: selectedBundleData.totalPrice,
        variant: {
          name: variantData.variant.name || "",
          imageUrl: variantData.variant.imageUrl || "",
        },
      };

      const mergedLineItems: CheckoutSummaryItem[] = [
        ...existingFilteredLineItems,
        newLineItem,
      ];

      setOptimisticBundleId(bundleId);
      setOptimisticCartLines(mergedLineItems);

      const bundleWorkPromise = (async () => {
        setBundleMutationPending(true);
        try {
          await updateLineItemsInSDK(convertToSDKLineItems([newLineItem]));
        } catch (error) {
          console.error("[useCheckoutLogic] Error in handleBundleChange:", {
            error,
            bundleId,
          });
          if (seq === bundleMutationSeqRef.current) {
            toast.error("Failed to update your selection. Please try again.");
            setOptimisticBundleId(null);
            setOptimisticCartLines(null);
            try {
              await refresh();
            } catch (refreshError) {
              console.error("[useCheckoutLogic] refresh after bundle error:", {
                refreshError,
              });
            }
          }
        } finally {
          setBundleMutationPending(false);
        }
      })();

      bundleWorkRef.current = bundleWorkPromise;
      bundleWorkPromise.finally(() => {
        if (bundleWorkRef.current === bundleWorkPromise) {
          bundleWorkRef.current = null;
        }
      });
      await bundleWorkPromise;
    },
    [
      bundleMutationPending,
      bundles,
      sdkCheckout?.summary?.items,
      getVariant,
      pluginConfig.defaultCurrency,
      updateLineItemsInSDK,
      convertToSDKLineItems,
      refresh,
    ],
  );

  const clearOptimisticBump = useCallback((variantId: string) => {
    setOptimisticBumpSelection((prev) => {
      if (!(variantId in prev)) return prev;
      const next = { ...prev };
      delete next[variantId];
      return next;
    });
  }, []);

  const handleOrderBumpToggle = useCallback(
    async (variantId: string, selected: boolean) => {
      if (orderBumpMutationPending) return;

      const toggle = orderBumpToggleByVariantId.get(variantId);
      if (!toggle) {
        console.warn(
          "[useCheckoutLogic] Order bump config not found for variantId:",
          variantId,
        );
        return;
      }

      setOptimisticBumpSelection((prev) => ({
        ...prev,
        [variantId]: selected,
      }));

      const bumpWorkPromise = (async () => {
        setOrderBumpMutationPending(true);
        try {
          const result = await toggle(selected);
          if (
            result &&
            typeof result === "object" &&
            "success" in result &&
            result.success === false
          ) {
            toast.error("Failed to update add-on. Please try again.");
            clearOptimisticBump(variantId);
            await refresh();
          }
        } catch (error) {
          console.error("[useCheckoutLogic] Error in handleOrderBumpToggle:", {
            error,
            variantId,
            selected,
          });
          toast.error("Failed to update add-on. Please try again.");
          clearOptimisticBump(variantId);
          try {
            await refresh();
          } catch (refreshError) {
            console.error("[useCheckoutLogic] refresh after bump error:", {
              refreshError,
            });
          }
        } finally {
          setOrderBumpMutationPending(false);
        }
      })();

      bumpWorkRef.current = bumpWorkPromise;
      bumpWorkPromise.finally(() => {
        if (bumpWorkRef.current === bumpWorkPromise) {
          bumpWorkRef.current = null;
        }
      });
      await bumpWorkPromise;
    },
    [
      orderBumpMutationPending,
      orderBumpToggleByVariantId,
      refresh,
      clearOptimisticBump,
    ],
  );

  const prePurchaseCheckout = useCallback(async () => {
    if (!sdkCheckout?.checkoutSession?.id) {
      console.warn(
        "[useCheckoutLogic] Checkout session not available for pre-purchase refresh",
      );
      return false;
    }
    try {
      await (bundleWorkRef.current ?? Promise.resolve());
      await (bumpWorkRef.current ?? Promise.resolve());
      await refresh();
      return true;
    } catch (error) {
      console.error("[useCheckoutLogic] Error in prePurchaseCheckout:", {
        error,
      });
      return false;
    }
  }, [sdkCheckout?.checkoutSession?.id, refresh]);

  useEffect(() => {
    if (!optimisticBundleId) return;
    const bundle = bundles.find((b) => b.id === optimisticBundleId);
    if (bundle && currentVariantId === bundle.variantId) {
      setOptimisticBundleId(null);
      setOptimisticCartLines(null);
    }
  }, [optimisticBundleId, currentVariantId, bundles]);

  useEffect(() => {
    const items = sdkCheckout?.summary?.items;
    if (!items?.length) return;
    setOptimisticBumpSelection((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const vid of Object.keys(next)) {
        const qty = items.find((r: any) => r.variantId === vid)?.quantity ?? 0;
        const want = next[vid];
        if (qty > 0 === want) {
          delete next[vid];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [sdkCheckout?.summary?.items]);

  useEffect(() => {
    if (sdkError) {
      console.error("[useCheckoutLogic] SDK error detected:", {
        error: sdkError,
        checkoutToken,
        errorMessage:
          sdkError instanceof Error ? sdkError.message : String(sdkError),
      });
    }
  }, [sdkError, checkoutToken]);

  useEffect(() => {
    if (
      checkoutToken ||
      sdkCheckout ||
      hasInitializedRef.current ||
      isInitFailed
    ) {
      return;
    }

    hasInitializedRef.current = true;
    const initialVariantId = pluginConfig.variants.double;

    if (!initialVariantId) {
      console.warn("[useCheckoutLogic] No initial variant ID configured");
      return;
    }

    const initialLineItems: CheckoutLineItem[] = [
      {
        variantId: initialVariantId,
        quantity: 1,
        priceId:
          pluginConfig.prices[
            initialVariantId as keyof typeof pluginConfig.prices
          ],
      },
    ];

    init({
      storeId,
      lineItems: initialLineItems,
      ...(window.location.search
        ? {
            customerMetadata: {
              queryParams: window.location.search,
            },
          }
        : {}),
    }).catch((error) => {
      console.error("[useCheckoutLogic] Failed to initialize checkout:", {
        error,
        initialVariantId,
        storeId,
      });
      setIsInitFailed(true);
    });
  }, [
    checkoutToken,
    sdkCheckout,
    init,
    pluginConfig.variants.double,
    pluginConfig.prices,
    storeId,
    isInitFailed,
  ]);

  const isUpdating = sdkIsLoading || bundleMutationPending;

  const hasOptimisticOverlay = useMemo(
    () =>
      Boolean(
        optimisticBundleId ||
          (optimisticCartLines?.length ?? 0) > 0 ||
          Object.keys(optimisticBumpSelection).length > 0,
      ),
    [
      optimisticBundleId,
      optimisticCartLines,
      optimisticBumpSelection,
    ],
  );

  return {
    isLoading: sdkIsLoading,
    checkout: sdkCheckout,
    getCheckout,
    lineItems,
    checkoutToken,
    refresh,
    error: sdkError,
    pluginConfig,
    bundles,
    selectedBundle,
    isUpdating,
    orderBumpsInCheckout,
    handleBundleChange,
    handleOrderBumpToggle,
    orderBumpMutationPending,
    prePurchaseCheckout,
    couponCodeInput,
    setCouponCodeInput: updateCouponCodeInput,
    couponDisplayLabel,
    isApplyingCoupon,
    couponError,
    canRemoveCoupon: Boolean(appliedPromotionId),
    applyCoupon,
    removeCoupon,
    updateCustomerAndSessionInfo: sdkUpdateCustomerAndSessionInfo,
    hasOptimisticOverlay,
  };
}
